"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  createExpertsAliveLipSync,
  type AssetProgress,
  type ExpertsAliveLipSyncOptions,
  type ExpertsAliveLipSyncRuntime,
  type RuntimeEvent,
  type RuntimeSnapshot,
} from "../web/local/experts-alive-lipsync.js";
import { FREE_PLAN_WATERMARK, type LipSyncState } from "../runtime/lip-sync-contract.js";
import {
  getLegacyDigitalHumanPool,
  useOptionalExpertsAlivePool,
  type DigitalHumanPoolCallbacks,
  type DigitalHumanRuntimeLease,
} from "./DigitalHumanPool";

export interface LocalDigitalHumanHandle {
  getRuntime: () => ExpertsAliveLipSyncRuntime | null;
  attachAudioElement: (audio: HTMLAudioElement) => () => void;
  detachAudioElement: () => void;
}

export interface LocalDigitalHumanProps extends Omit<
  ExpertsAliveLipSyncOptions,
  | "container"
  | "className"
  | "onError"
  | "onEvent"
  | "onInputLevel"
  | "onProgress"
  | "onReady"
  | "onResolutionChange"
  | "onStateChange"
  | "watermark"
  | "watermarkText"
> {
  /**
   * Browser audio whose real playback lifecycle should drive speaking motion.
   * Prefer this over manually calling beginSpeaking/returnToIdle for file audio.
   */
  audioElement?: HTMLAudioElement | null;
  className?: string;
  motionClassName?: string;
  onError?: (error: Error) => void;
  onEvent?: (event: RuntimeEvent) => void;
  onInputLevel?: (level: number) => void;
  onProgress?: (progress: AssetProgress) => void;
  /** Compatibility alias: pooled runtimes invoke this when detached, not destroyed. */
  onRuntimeDisposed?: (runtime: ExpertsAliveLipSyncRuntime) => void;
  onRuntimeDetached?: (runtime: ExpertsAliveLipSyncRuntime) => void;
  onRuntimeDestroyed?: (runtime: ExpertsAliveLipSyncRuntime) => void;
  onRuntimeReady?: (runtime: ExpertsAliveLipSyncRuntime, snapshot: RuntimeSnapshot) => void;
  onFirstFrameReady?: (runtime: ExpertsAliveLipSyncRuntime, snapshot: RuntimeSnapshot) => void;
  onResolutionChange?: ExpertsAliveLipSyncOptions["onResolutionChange"];
  onStateChange?: (state: LipSyncState, previous: LipSyncState) => void;
  /** Provider-managed identity. Keep stable across view-size/card changes. */
  poolKey?: string;
  /** Backward-compatible alias that uses the provider or the legacy singleton pool. */
  persistentKey?: string;
  poolGroupKey?: string;
  estimatedBytes?: number;
  /** Restored after an asynchronous pooled acquisition becomes ready. */
  desiredState?: "idle" | "speaking";
  placeholder?: ReactNode;
  style?: CSSProperties;
  watermarkText?: string;
}

/**
 * React lifecycle adapter for the local Experts Alive Web runtime.
 * React owns mounting/unmounting; the SDK runtime owns media, caching,
 * resolution, timing, playback state, microphone metering, and cleanup.
 */
export const LocalDigitalHuman = forwardRef<LocalDigitalHumanHandle, LocalDigitalHumanProps>(
  function LocalDigitalHuman(props, ref) {
    const providerPool = useOptionalExpertsAlivePool();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const runtimeRef = useRef<ExpertsAliveLipSyncRuntime | null>(null);
    const runtimeReadyRef = useRef(false);
    const requestedAudioRef = useRef<HTMLAudioElement | null>(props.audioElement ?? null);
    const audioBindingCleanupRef = useRef<(() => void) | null>(null);
    const leaseRef = useRef<DigitalHumanRuntimeLease | null>(null);
    const callbacksRef = useRef(props);
    callbacksRef.current = props;
    requestedAudioRef.current = props.audioElement ?? requestedAudioRef.current;

    function detachAudioElement() {
      audioBindingCleanupRef.current?.();
      audioBindingCleanupRef.current = null;
      requestedAudioRef.current = null;
    }

    function attachAudioElement(audio: HTMLAudioElement) {
      audioBindingCleanupRef.current?.();
      audioBindingCleanupRef.current = null;
      requestedAudioRef.current = audio;
      const runtime = runtimeRef.current;
      if (!runtime || !runtimeReadyRef.current) {
        return () => {
          if (requestedAudioRef.current === audio) requestedAudioRef.current = null;
        };
      }
      const runtimeCleanup = runtime.attachAudioElement(audio);
      const cleanup = () => {
        if (audioBindingCleanupRef.current !== cleanup) return;
        runtimeCleanup();
        audioBindingCleanupRef.current = null;
        if (requestedAudioRef.current === audio) requestedAudioRef.current = null;
      };
      audioBindingCleanupRef.current = cleanup;
      return cleanup;
    }

    useImperativeHandle(ref, () => ({
      getRuntime: () => runtimeRef.current,
      attachAudioElement,
      detachAudioElement,
    }), []);

    useEffect(() => {
      const audio = props.audioElement ?? null;
      if (!audio) {
        detachAudioElement();
        return;
      }
      requestedAudioRef.current = audio;
      if (!runtimeReadyRef.current) return;
      return attachAudioElement(audio);
    }, [props.audioElement]);

    const {
      assetManifestUrl,
      autoDowngradeWidth,
      autoUpgradeWidth,
      bandwidthSaver,
      freezeMouthDuringOutputSilence,
      honorBrowserDataSaver,
      locale,
      motionClassName,
      outputSilenceHoldMs,
      plan,
      platform,
      resolution,
      settingsUrl,
    } = props;

    const effectivePoolKey = props.poolKey ?? props.persistentKey;

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      let active = true;
      runtimeReadyRef.current = false;
      const runtimeOptions = {
        assetManifestUrl,
        autoDowngradeWidth,
        autoUpgradeWidth,
        bandwidthSaver,
        freezeMouthDuringOutputSilence,
        className: motionClassName ?? "",
        container,
        honorBrowserDataSaver,
        locale,
        outputSilenceHoldMs,
        plan,
        platform: platform ?? "webApp",
        resolution,
        settingsUrl,
      };
      const callbacks: DigitalHumanPoolCallbacks = {
        onError: (error) => callbacksRef.current.onError?.(error),
        onEvent: (event) => callbacksRef.current.onEvent?.(event),
        onInputLevel: (level) => callbacksRef.current.onInputLevel?.(level),
        onProgress: (progress) => callbacksRef.current.onProgress?.(progress),
        onResolutionChange: (next, previous) => {
          callbacksRef.current.onResolutionChange?.(next, previous);
        },
        onStateChange: (state, previous) => {
          container.dataset.eaReactState = state;
          callbacksRef.current.onStateChange?.(state, previous);
        },
        onRuntimeDestroyed: (destroyedRuntime: ExpertsAliveLipSyncRuntime) => {
          callbacksRef.current.onRuntimeDestroyed?.(destroyedRuntime);
        },
      };
      let lease: DigitalHumanRuntimeLease | null = null;
      let runtime: ExpertsAliveLipSyncRuntime;
      let runtimeReady: Promise<RuntimeSnapshot>;
      let firstFrameReady: Promise<RuntimeSnapshot>;
      try {
        if (effectivePoolKey) {
          const pool = providerPool ?? getLegacyDigitalHumanPool();
          lease = pool.acquire({
            ...runtimeOptions,
            className: motionClassName ?? "",
            estimatedBytes: props.estimatedBytes,
            groupKey: props.poolGroupKey,
            key: effectivePoolKey,
          }, container, callbacks);
          leaseRef.current = lease;
          runtime = lease.runtime;
          runtimeReady = lease.ready;
          firstFrameReady = lease.firstFrameReady;
        } else {
          runtime = createExpertsAliveLipSync({
            ...runtimeOptions,
            ...callbacks,
            className: motionClassName ?? "",
            container,
            watermark: false,
          });
          runtimeReady = runtime.initialize();
          firstFrameReady = runtimeReady.then((snapshot) => new Promise<RuntimeSnapshot>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve(snapshot)));
          }));
        }
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error(String(cause));
        callbacksRef.current.onError?.(error);
        return;
      }
      runtimeRef.current = runtime;
      void runtimeReady
        .then((snapshot) => {
          if (!active || (lease && !lease.isCurrent())) return;
          runtimeReadyRef.current = true;
          const requestedAudio = requestedAudioRef.current;
          if (requestedAudio) attachAudioElement(requestedAudio);
          if (callbacksRef.current.desiredState === "speaking"
            && (!requestedAudio || requestedAudio.paused || requestedAudio.ended)) {
            runtime.beginSpeaking({ source: "react-desired-state", holdThroughSilence: true });
            lease?.diagnose("speaking-replayed", { source: "react-desired-state" });
            callbacksRef.current.onEvent?.({
              type: "speaking-replayed",
              at: performance.now(),
              detail: { poolKey: effectivePoolKey },
            });
          }
          callbacksRef.current.onRuntimeReady?.(runtime, snapshot);
        })
        .catch(() => undefined);
      void firstFrameReady
        .then((snapshot) => {
          if (!active || (lease && !lease.isCurrent())) return;
          callbacksRef.current.onFirstFrameReady?.(runtime, snapshot);
        })
        .catch(() => undefined);

      return () => {
        active = false;
        runtimeReadyRef.current = false;
        audioBindingCleanupRef.current?.();
        audioBindingCleanupRef.current = null;
        if (runtimeRef.current === runtime) runtimeRef.current = null;
        if (lease) {
          callbacksRef.current.onRuntimeDetached?.(runtime);
          callbacksRef.current.onRuntimeDisposed?.(runtime);
          lease.release("react-unmount");
          if (leaseRef.current === lease) leaseRef.current = null;
        } else {
          runtime.destroy();
          callbacksRef.current.onRuntimeDestroyed?.(runtime);
          callbacksRef.current.onRuntimeDisposed?.(runtime);
        }
      };
    }, [
      assetManifestUrl,
      autoDowngradeWidth,
      autoUpgradeWidth,
      bandwidthSaver,
      freezeMouthDuringOutputSilence,
      honorBrowserDataSaver,
      locale,
      motionClassName,
      outputSilenceHoldMs,
      plan,
      platform,
      props.estimatedBytes,
      props.poolGroupKey,
      effectivePoolKey,
      providerPool,
      resolution,
      settingsUrl,
    ]);

    useEffect(() => {
      const runtime = runtimeRef.current;
      if (!runtime || !runtimeReadyRef.current) return;
      if (props.desiredState === "speaking") {
        const audio = requestedAudioRef.current;
        if (!audio || audio.paused || audio.ended) {
          runtime.beginSpeaking({ source: "react-desired-state", holdThroughSilence: true });
        }
      } else if (props.desiredState === "idle") {
        runtime.returnToIdle("react-desired-state");
      }
    }, [props.desiredState]);

    const isFreePlan = (plan ?? "free") === "free";
    const watermarkText = props.watermarkText?.trim() || "EXPERTS ALIVE";

    return (
      <div
        className={props.className}
        data-ea-plan={plan ?? "free"}
        style={{ ...props.style, position: "relative" }}
      >
        <div
          ref={containerRef}
          style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: "inherit" }}
        >
          {props.placeholder}
        </div>
        {isFreePlan && (
          <div
            aria-label={`${watermarkText} Free plan watermark`}
            data-ea-free-watermark="true"
            style={{
              alignItems: "center",
              backdropFilter: "blur(8px)",
              background: "rgba(5, 15, 12, 0.76)",
              border: `${FREE_PLAN_WATERMARK.borderWidth}px solid rgba(255, 255, 255, 0.32)`,
              borderRadius: 999,
              bottom: FREE_PLAN_WATERMARK.bottomInset,
              color: "white",
              display: "flex",
              fontSize: FREE_PLAN_WATERMARK.fontSize,
              fontWeight: 800,
              gap: FREE_PLAN_WATERMARK.gap,
              left: "50%",
              letterSpacing: "0.13em",
              lineHeight: 1,
              padding: `${FREE_PLAN_WATERMARK.verticalPadding}px ${FREE_PLAN_WATERMARK.horizontalPadding}px`,
              pointerEvents: "none",
              position: "absolute",
              textTransform: "uppercase",
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              zIndex: 10,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                background: "#a4ff62",
                borderRadius: "50%",
                boxShadow: `0 0 0 ${FREE_PLAN_WATERMARK.dotRingWidth}px rgba(164, 255, 98, 0.18)`,
                height: FREE_PLAN_WATERMARK.dotSize,
                width: FREE_PLAN_WATERMARK.dotSize,
              }}
            />
            {watermarkText}
          </div>
        )}
      </div>
    );
  },
);
